import { createCipheriv, createDecipheriv, randomBytes } from 'node:crypto';

import {
  buildAad,
  formatV1,
  formatV2,
  parseEnvelope,
  encodeHeader,
  PREFIX,
  type DecryptContext,
} from './envelope.js';
import { getKeyring, requireActiveKey, resolveKeyByKid, type KeyEntry } from './keyring.js';
import {
  AeadAuthFailureError,
  AppKeyNotInKeyringError,
  CustomerKekUnavailableError,
  DecryptContextRequiredError,
  PlatformKekUnavailableError,
} from './secret-errors.js';
import { isV2WritesArmed } from './writer-arming.js';

const ALGORITHM = 'aes-256-gcm';
const IV_LENGTH = 12;
const AUTH_TAG_LENGTH = 16;

/** GCM-decrypt `data` (ciphertext||tag) under `key`, optionally binding `aad`. Throws on auth failure. */
function gcmDecrypt(key: Buffer, iv: Buffer, data: Buffer, aad?: Buffer): string {
  const ciphertext = data.subarray(0, data.length - AUTH_TAG_LENGTH);
  const tag = data.subarray(data.length - AUTH_TAG_LENGTH);
  const decipher = createDecipheriv(ALGORITHM, key, iv, { authTagLength: AUTH_TAG_LENGTH });
  if (aad) decipher.setAAD(aad);
  decipher.setAuthTag(tag);
  return Buffer.concat([decipher.update(ciphertext), decipher.final()]).toString('utf8');
}

/**
 * Encrypt a plaintext string for at-rest storage.
 *
 * Returns a **v2 `scheme a`** envelope (`enc:2:<header>:<iv>:<ct+tag>`, org- and
 * field-grained AAD) when BOTH (a) v2 writes are armed for this process
 * (`setV2WritesArmed(true)`, ADR-0061 release A2 / ENG-8777) AND (b) a
 * `DecryptContext` is supplied to bind the AAD. Otherwise returns the legacy
 * **v1** envelope (`enc:<iv>:<ct+tag>`, no AAD) under the active app key.
 *
 * The dual condition is the incremental-safe writer contract:
 *   - Disarmed (the default, and every un-converged fleet) ⇒ always v1, so no v2
 *     blob can reach a reader that can't parse it (the A1→A2 ordering; once a v2
 *     blob lands, reverting to a v1-only parser bricks it — ADR-0061 Part 1).
 *   - Armed but context-less ⇒ v1, so a call site not yet migrated to thread its
 *     `org/table/column/field/row` keeps writing the un-AAD'd form safely. The
 *     v2 AAD is not optional (a context-less v2 could not be authenticated on
 *     read), so a missing context degrades to v1 rather than an unbound v2.
 *
 * Both paths stamp under the ACTIVE key only (`requireActiveKey`, via
 * `sealV2SchemeA` for v2) — never the retiring slot.
 */
export function encryptSecret(plaintext: string, context?: DecryptContext): string {
  if (context && isV2WritesArmed()) {
    return sealV2SchemeA(plaintext, context);
  }
  const { key } = requireActiveKey();
  const iv = randomBytes(IV_LENGTH);
  const cipher = createCipheriv(ALGORITHM, key, iv, { authTagLength: AUTH_TAG_LENGTH });
  const encrypted = Buffer.concat([cipher.update(plaintext, 'utf8'), cipher.final()]);
  const tag = cipher.getAuthTag();
  return formatV1(iv, Buffer.concat([encrypted, tag]));
}

/**
 * Whether this process is a **real deployment** (as opposed to a local dev box).
 *
 * The signal is the presence of `AUGMENTED_SECRETS_SSM_PREFIX` — set by
 * `sst.config.ts` on exactly the stages whose deploy workflow provisions secrets
 * into SSM (the same switch `secrets-bootstrap` uses to decide SSM is
 * authoritative). It is **presence-based on purpose**: ADR-0061 Part 4 requires
 * gating on "is a real deployment", NOT on `prefix ∈ {prod,test}` — a future
 * stage with real credentials but a non-prod/test name (`staging-uk`, a
 * customer stage) has its own prefix and must fail closed too. Local dev has no
 * prefix (keys come from `.env`), so it is the only place plaintext fallback is
 * tolerated.
 */
export function isRealDeployment(): boolean {
  const prefix = process.env['AUGMENTED_SECRETS_SSM_PREFIX'];
  return typeof prefix === 'string' && prefix.length > 0;
}

/**
 * Encrypt a field value for at-rest storage, **failing closed on a real
 * deployment** (ADR-0061 Part 4). This is the writer every sensitive-field
 * helper (`encryptSensitiveSettings`, `encryptIntegrationCredentials`,
 * `encryptChannelConfig`) funnels through instead of a bare
 * `try { encryptSecret } catch { /* plaintext *\/ }`.
 *
 * On a missing/broken key:
 *   - **Real deployment** → THROW (never silently persist a customer credential
 *     as plaintext — the live bug this closes). The typed
 *     `AppKeyNotInKeyringError`/`PlatformKekUnavailableError` propagates to the
 *     caller, which must abort the write.
 *   - **Local dev** (no SSM prefix) → return the plaintext so a laptop without a
 *     key stays functional — but emit a structured warning so the fallback is
 *     never invisible (the "emit a metric on every catch, even in dev" clause).
 *
 * Callers still gate on `typeof value === 'string' && value && !isEncrypted`
 * before calling, so this only ever sees a fresh plaintext string.
 */
export function encryptAtRest(plaintext: string, context?: DecryptContext): string {
  try {
    return encryptSecret(plaintext, context);
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    if (isRealDeployment()) {
      // Fail closed: a deployed stage must never downgrade a secret to plaintext.
      console.error(
        '[crypto] at-rest encryption failed on a deployed stage — refusing to store plaintext',
        { error: message },
      );
      throw err;
    }
    // Local dev only: tolerate plaintext, but make the fallback observable.
    console.warn(
      '[crypto] at-rest encryption unavailable — storing plaintext (local dev only)',
      { error: message },
    );
    return plaintext;
  }
}

/**
 * Seal a plaintext into a **v2 scheme-`a`** envelope under the active key, binding
 * the org+field-grained AAD. `encryptSecret` delegates here when v2 writes are
 * armed and a `DecryptContext` is supplied (ADR-0061 A2 / ENG-8777); it is also
 * exported directly for tests and for callers that unconditionally want a v2
 * write.
 */
export function sealV2SchemeA(plaintext: string, context: DecryptContext): string {
  const active = requireActiveKey();
  const headerBytes = encodeHeader({ s: 'a', kid: active.kid });
  const aad = buildAad(headerBytes, context);
  const iv = randomBytes(IV_LENGTH);
  const cipher = createCipheriv(ALGORITHM, active.key, iv, { authTagLength: AUTH_TAG_LENGTH });
  cipher.setAAD(aad);
  const encrypted = Buffer.concat([cipher.update(plaintext, 'utf8'), cipher.final()]);
  const tag = cipher.getAuthTag();
  return formatV2(headerBytes, iv, Buffer.concat([encrypted, tag]));
}

/**
 * Decrypt a value produced by the at-rest envelope. Returns plaintext.
 *
 * Behaviour by input:
 *   - Not `enc:`-prefixed → returned verbatim (pre-encryption backward compat).
 *   - **v1** (`enc:iv:ct`) → trial-decrypted across the keyring (active first);
 *     no AAD. `context` is ignored.
 *   - **v2 scheme a** → key resolved by `kid`; AAD reconstructed from the header
 *     bytes + `context` (REQUIRED for v2).
 *   - **v2 scheme k/c** → dispatched but not decryptable in this build; throws the
 *     typed KEK-unavailable error (ADR-0062 implements the unwrap).
 *
 * Never silently returns ciphertext (the ENG-8507 lesson): a resolvable-shape but
 * undecryptable blob throws a typed `SecretDecryptError` (see secret-errors.ts).
 */
export function decryptSecret(encoded: string, context?: DecryptContext): string {
  if (!encoded.startsWith(PREFIX)) {
    // Not encrypted — return as-is (backward compat for pre-encryption values).
    return encoded;
  }

  const parsed = parseEnvelope(encoded);

  if (parsed.version === 1) {
    return decryptV1(parsed.iv, parsed.data);
  }

  switch (parsed.header.s) {
    case 'a': {
      const entry = resolveKeyByKid(parsed.header.kid);
      if (!entry) {
        throw new AppKeyNotInKeyringError(
          `no app key with kid ${parsed.header.kid} in the keyring`,
        );
      }
      if (!context) {
        throw new DecryptContextRequiredError(
          'decryptSecret: a v2 envelope requires a DecryptContext to reconstruct its AAD',
        );
      }
      const aad = buildAad(parsed.headerBytes, context);
      try {
        return gcmDecrypt(entry.key, parsed.iv, parsed.data, aad);
      } catch (cause) {
        throw new AeadAuthFailureError(
          `scheme-a decrypt failed authentication for kid ${parsed.header.kid} (AAD mismatch or tampering)`,
          { cause },
        );
      }
    }
    // Schemes k/c are grammar this build parses but cannot unwrap — the KEK/DEK
    // path is ADR-0062. A blob under either cannot exist in a 0061-only fleet
    // (the writer never emits them); these throw so dispatch is total and typed.
    case 'k':
      throw new PlatformKekUnavailableError(
        'scheme k (platform-CMK DEK) unwrap is not available in this build (ADR-0062)',
      );
    case 'c':
      throw new CustomerKekUnavailableError(
        'scheme c (customer-CMK DEK) unwrap is not available in this build (ADR-0062)',
      );
  }
}

/** Trial-decrypt a v1 (keyless) blob across the keyring, active key first. */
function decryptV1(iv: Buffer, data: Buffer): string {
  const { trialOrder } = getKeyring();
  if (trialOrder.length === 0) {
    throw new AppKeyNotInKeyringError('the app-key keyring is empty (AUTH_ENCRYPTION_KEY unset)');
  }
  let lastError: unknown;
  for (const entry of trialOrder as readonly KeyEntry[]) {
    try {
      return gcmDecrypt(entry.key, iv, data);
    } catch (err) {
      lastError = err;
    }
  }
  // v1 carries no kid, so an all-keys-fail is indistinguishable between "the
  // writing key isn't in the ring" and corruption; the app-key signal is the
  // actionable one during a drain.
  throw new AppKeyNotInKeyringError(
    'no keyring key could decrypt a v1 blob (writing key absent or ciphertext corrupt)',
    { cause: lastError },
  );
}

/** Check if a value is already encrypted (has the `enc:` prefix). */
export function isEncrypted(value: string): boolean {
  return value.startsWith(PREFIX);
}
